import type { JobListItem } from '../types/jobs';
import {
  isTerminal,
  type AdmissionStatus,
  type JobStatus,
} from '../types/monitoring';

// A running/stuck eval-set older than this is "long-running" — the thing we
// want to surface so an old job nobody noticed doesn't keep burning compute.
const LONG_RUNNING_MS = 24 * 60 * 60 * 1000;

export function isLongRunning(item: JobListItem): boolean {
  if (item.status !== 'running' && item.status !== 'stuck') return false;
  const age = Date.now() - new Date(item.created_at).getTime();
  return Number.isFinite(age) && age > LONG_RUNNING_MS;
}

export function isStatusUnavailable(
  status: JobStatus | null,
  admission?: AdmissionStatus | null
): boolean {
  return status === 'pending' && admission?.state === 'unknown';
}

export function labelForStatus(
  status: JobStatus,
  admission?: AdmissionStatus | null
): string {
  if (isStatusUnavailable(status, admission)) return 'Status unavailable';
  switch (status) {
    case 'pending':
      return 'Queued';
    case 'running':
      return 'Running';
    case 'waiting_for_capacity':
      return 'Waiting for capacity';
    case 'importing':
      return 'Importing samples';
    case 'stuck':
      return 'Stuck';
    case 'complete':
      return 'Complete';
    case 'failed':
      return 'Failed';
    case 'deleted':
      return 'Deleted';
  }
}

export type StatusTone = 'neutral' | 'green' | 'warn' | 'err';

/**
 * One tone per status, shared by the jobs list, the status bar and the status
 * page. Green marks current activity, amber and red mark attention states;
 * everything else (queued, waiting, finished, deleted) reads as neutral text.
 */
export function toneForStatus(status: JobStatus): StatusTone {
  switch (status) {
    case 'running':
    case 'importing':
      return 'green';
    case 'stuck':
      return 'warn';
    case 'failed':
      return 'err';
    case 'pending':
    case 'waiting_for_capacity':
    case 'complete':
    case 'deleted':
      return 'neutral';
  }
}

const TEXT_COLOR: Record<StatusTone, string> = {
  neutral: 'text-ink-2',
  green: 'text-ok',
  warn: 'text-warn',
  err: 'text-err',
};

const DOT_COLOR: Record<StatusTone, string> = {
  neutral: 'text-neutral-mark',
  green: 'text-ok-mark',
  warn: 'text-warn-mark',
  err: 'text-err-mark',
};

/** Text color class for a status label. */
export function textClassForStatus(status: JobStatus): string {
  return TEXT_COLOR[toneForStatus(status)];
}

/** Classes for a `.dot`: tone color, pulsing while the job is in flight. */
export function dotClassForStatus(status: JobStatus): string {
  const live = !isTerminal(status) && status !== 'pending';
  return `dot ${DOT_COLOR[toneForStatus(status)]}${live ? ' dot-live' : ''}`;
}

export function friendlyTroubleReason(
  reason: string | null | undefined
): string | null {
  if (reason == null) return null;
  switch (reason) {
    case 'OOMKilled':
      return 'runner out of memory';
    case 'ImagePullBackOff':
    case 'ErrImagePull':
      return 'image pull failing';
    case 'CrashLoopBackOff':
      return 'container crash looping';
    case 'CreateContainerError':
    case 'CreateContainerConfigError':
      return 'container creation failed';
    case 'Unschedulable':
      return 'waiting for cluster capacity';
    default:
      return reason;
  }
}

export function priorityLabel(
  priority: number | null | undefined
): string | null {
  if (priority == null) return null;
  if (priority === -100) return 'Low';
  if (priority === 0) return 'Medium';
  if (priority === 100) return 'High';
  return String(priority);
}

export function admissionDiagnostics(
  admission: AdmissionStatus | null | undefined
): string[] {
  if (admission == null) return [];
  const diagnostics: string[] = [];
  const priority = priorityLabel(admission.priority);
  if (priority !== null) diagnostics.push(`${priority} priority`);
  if (admission.state === 'queued' || admission.state === 'pending') {
    diagnostics.push(
      admission.message ??
        admission.reason ??
        (admission.state === 'pending'
          ? 'waiting for admission'
          : `waiting for ${admission.queue ?? 'cluster'} quota`)
    );
  }
  return diagnostics;
}
